/**
 * Аналітика підтримки — пульт контролю (ч.1018, SPEC 8.3).
 *
 * Відповідає на три питання: як працює підтримка, де агент помиляється,
 * чого бракує у довіднику.
 *
 * Свідомо БЕЗ прикрас: голі числа і списки. Показник, який нічого не
 * означає, гірший за його відсутність — тому «успіх агента» підписаний
 * як ТЕХНІЧНИЙ, а обмеження групування повторюваних тем визнано просто
 * у тексті, а не приховано.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { supportAnalytics, type EvalDetail } from '@/api/supportAnalytics';
import { supportConsole } from '@/api/supportConsole';

const PERIODS = [7, 30, 90, 365];

function Metric({ label, value, hint, warn }:
  { label: string; value: string | number; hint?: string; warn?: boolean }) {
  return (
    <div className={`an__metric${warn ? ' an__metric--warn' : ''}`} title={hint}>
      <div className="an__metric-value">{value}</div>
      <div className="an__metric-label">{label}</div>
    </div>
  );
}

export default function SupportAnalyticsPage() {
  const [days, setDays] = useState(30);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['sup-analytics', days],
    queryFn: () => supportAnalytics.get(days),
  });

  // ч.1019: чернетки доповнення довідника — поруч із показниками
  // прогалин, бо це прямий спосіб їх закрити.
  const drafts = useQuery({
    queryKey: ['kb-suggestions'],
    queryFn: supportConsole.suggestions,
  });
  const decide = useMutation({
    mutationFn: ({ id, ok }: { id: string; ok: boolean }) =>
      ok ? supportConsole.acceptSuggestion(id, editing[id] ?? null)
         : supportConsole.rejectSuggestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kb-suggestions'] }),
  });

  // ч.1020: контроль якості агента. Показуємо ОСТАННІЙ результат —
  // щоб стан було видно одразу, без натискання кнопки.
  const lastEval = useQuery({
    queryKey: ['agent-evals'],
    queryFn: supportAnalytics.lastEval,
  });
  const runEval = useMutation({
    mutationFn: supportAnalytics.runEvals,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-evals'] }),
  });
  const ev = lastEval.data;

  const d = q.data;
  const t = d?.totals;

  return (
    <div className="card sup">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Аналітика підтримки</h2>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {PERIODS.map((p) => (
            <button key={p} type="button"
              className={days === p ? 'primary' : undefined}
              onClick={() => setDays(p)}>
              {p} днів
            </button>
          ))}
        </div>
      </div>

      {q.isLoading && <div className="sup__muted">Завантаження…</div>}
      {q.isError && <div className="sup__muted">Не вдалося завантажити.</div>}

      {t && (
        <>
          <div className="an__grid">
            <Metric label="звернень" value={t.cases_total} />
            <Metric label="відкритих" value={t.cases_open} />
            <Metric label="без виконавця" value={t.cases_unassigned}
              warn={t.cases_unassigned > 0}
              hint="Ніхто не взяв у роботу" />
            <Metric label="відповідей агента" value={t.agent_answers} />
            <Metric label="передано людині" value={t.agent_escalations} />
            <Metric label="завдань без збою" value={t.agent_success}
              hint="ТЕХНІЧНА успішність виконання завдань — не означає, що
                    користувачу сподобалась відповідь" />
            <Metric label="завдань впало" value={t.agent_failed}
              warn={t.agent_failed > 0} />
            <Metric label="перша відповідь, хв"
              value={t.avg_first_reply_minutes ?? '—'} />
            <Metric label="пошуків у довідці" value={t.help_searches} />
            <Metric label="пошуків без результату" value={t.help_searches_empty}
              warn={t.help_searches_empty > 0}
              hint="Прямий список, чого бракує у довіднику" />
            <Metric label="оцінок «корисно»" value={t.articles_helpful} />
            <Metric label="запитів на консультацію" value={t.consulting_requests}
              hint="Агент відмовив (це не його жанр) — тема лишилась
                    у статистиці попиту" />
          </div>

          <div className="an__note">
            <b>«Завдань без збою»</b> — це технічна успішність виконання
            завдань агента, а <b>не</b> те, що користувачу сподобалась
            відповідь. Задоволеність видно з оцінок статей і звернень.
          </div>

          <section className="an__evals">
            <div className="an__evals-head">
              <h3>Контроль якості агента</h3>
              <button type="button" className="primary"
                disabled={runEval.isPending}
                onClick={() => runEval.mutate()}>
                {runEval.isPending ? 'перевіряю…' : 'запустити перевірку'}
              </button>
            </div>
            <div className="sup__muted" style={{ paddingTop: 0 }}>
              Перевіряє тверді межі: чи не радить з обліку, чи не вигадує
              функції, чи не розкриває внутрішні відомості, чи правильно
              передає складні випадки людині. Безкоштовно — модель не
              викликається, нікому нічого не надсилається.
            </div>

            {!ev && <div className="sup__muted">Перевірку ще не запускали.</div>}
            {ev && (
              <>
                <div className="an__evals-sum">
                  <span className={ev.critical_failed > 0 ? 'an__bad' : 'an__good'}>
                    {ev.passed} з {ev.total}
                  </span>
                  {ev.critical_failed > 0 && (
                    <span className="an__tag an__tag--warn">
                      критичних помилок: {ev.critical_failed}
                    </span>
                  )}
                </div>
                {ev.details.filter((x: EvalDetail) => !x.passed).map((x) => (
                  <div key={x.code} className="an__row">
                    <span className="an__row-main">{x.title}</span>
                    {x.got && <span className="an__row-num" style={{
                      fontWeight: 400, fontSize: '0.75rem' }}>{x.got}</span>}
                    <span className={`an__tag${x.critical ? ' an__tag--warn' : ''}`}>
                      {x.critical ? 'критично' : 'увага'}
                    </span>
                  </div>
                ))}
                {ev.failed === 0 && (
                  <div className="sup__muted">Усі перевірки пройдено.</div>
                )}
              </>
            )}
          </section>

          <div className="an__cols">
            <section>
              <h3>Які сторінки породжують питання</h3>
              {d!.top_routes.length === 0 && <div className="sup__muted">Немає даних.</div>}
              {d!.top_routes.map((r) => (
                <div key={r.route_pattern ?? '—'} className="an__row">
                  <span className="an__row-main">{r.route_pattern ?? '(не вказано)'}</span>
                  <span className="an__row-num">{r.cases}</span>
                  {!r.has_article && (
                    <span className="an__tag an__tag--warn" title="Під цю сторінку
                      немає статті у довіднику — агент змушений ескалювати">
                      немає статті
                    </span>
                  )}
                </div>
              ))}
            </section>

            <section>
              <h3>Шукали, але не знайшли</h3>
              {d!.empty_searches.length === 0 && <div className="sup__muted">Немає даних.</div>}
              {d!.empty_searches.map((s) => (
                <div key={s.query} className="an__row">
                  <span className="an__row-main">{s.query}</span>
                  <span className="an__row-num">{s.count}</span>
                </div>
              ))}
            </section>

            <section>
              <h3>Попит на консультації</h3>
              <div className="sup__muted" style={{ paddingTop: 0 }}>
                Теми, де агент чесно відмовив — це не його жанр.
              </div>
              {d!.consulting_topics.map((s) => (
                <div key={s.topic} className="an__row">
                  <span className="an__row-main">{s.topic}</span>
                  <span className="an__row-num">{s.count}</span>
                </div>
              ))}
            </section>

            <section>
              <h3>Статті: перегляди й оцінки</h3>
              {d!.top_articles.length === 0 && <div className="sup__muted">Немає даних.</div>}
              {d!.top_articles.map((a) => (
                <div key={a.slug} className="an__row">
                  <button type="button" className="an__row-main an__link"
                    onClick={() => navigate(`/help/${a.slug}`)}>
                    {a.title}
                  </button>
                  <span className="an__row-num">{a.views}</span>
                  {a.not_helpful > 0 && (
                    <span className="an__tag an__tag--warn"
                      title="Стаття не допомогла">
                      ✕{a.not_helpful}
                    </span>
                  )}
                  {a.helpful > 0 && (
                    <span className="an__tag">✓{a.helpful}</span>
                  )}
                </div>
              ))}
            </section>

            <section>
              <h3>
                Чернетки довідника
                {(drafts.data?.length ?? 0) > 0 && ` (${drafts.data!.length})`}
              </h3>
              <div className="sup__muted" style={{ paddingTop: 0 }}>
                Зроблені з відповідей підтримки. Прийняття дописує статтю —
                наступний користувач отримає відповідь без звернення.
              </div>
              {(drafts.data?.length ?? 0) === 0 && (
                <div className="sup__muted">Чернеток немає.</div>
              )}
              {(drafts.data ?? []).map((s) => (
                <div key={s.id} className="an__draft">
                  <div className="an__draft-q">{s.question}</div>
                  <textarea className="an__draft-a" rows={5}
                    value={editing[s.id] ?? s.answer}
                    onChange={(e) => setEditing((p) =>
                      ({ ...p, [s.id]: e.target.value }))} />
                  <div className="an__draft-meta">
                    {s.article_title
                      ? `допишеться у: ${s.article_title}`
                      : `статті під сторінку ${s.route_pattern ?? '—'} немає — `
                        + 'потрібна нова'}
                  </div>
                  <div className="sup__actions">
                    <button type="button" disabled={decide.isPending}
                      onClick={() => decide.mutate({ id: s.id, ok: false })}>
                      відхилити
                    </button>
                    <button type="button" className="primary"
                      disabled={decide.isPending || !s.article_slug}
                      title={s.article_slug ? undefined
                        : 'Немає статті під цю сторінку — спершу створіть її'}
                      onClick={() => decide.mutate({ id: s.id, ok: true })}>
                      прийняти
                    </button>
                  </div>
                </div>
              ))}
            </section>

            <section>
              <h3>Повторювані теми</h3>
              <div className="sup__muted" style={{ paddingTop: 0 }}>
                Групування суворе: знаходить повторені формулювання, але не
                обʼєднує однаковий зміст, написаний різними словами.
              </div>
              {d!.repeated.length === 0 && <div className="sup__muted">Повторів немає.</div>}
              {d!.repeated.map((r) => (
                <div key={r.title} className="an__row">
                  <span className="an__row-main">{r.title}</span>
                  <span className="an__row-num">{r.count}</span>
                </div>
              ))}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
